import { NgClass } from '@angular/common';
import { Component, computed, ElementRef, inject, input, Input, output, signal, viewChild } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { Dokument } from '@app/shared/classes/dokument';
import { DokumentTyp, DokumentTypId } from '@app/shared/classes/dokument-typ';
import { downloadFile } from '@app/shared/download-utils';
import { InjectUrlPipe } from '@app/shared/pipes/inject-url.pipe';
import { MarkedPipe } from '@app/shared/pipes/marked.pipe';
import { SettingPipe } from '@app/shared/pipes/setting.pipe';
import { BackendService } from '@app/shared/services/backend.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'temporary-dokument-upload',
  templateUrl: './temporary-dokument-upload.component.html',
  imports: [
    InjectUrlPipe,
    MarkedPipe,
    MatTooltip,
    NgClass,
    SettingPipe,
    TranslatePipe,
  ]
})
export class TemporaryDokumentUploadComponent {
  private readonly backendService = inject(BackendService);
  private readonly translateService = inject(TranslateService);

  protected readonly selectedDokumente = signal<Dokument[]>([]);
  protected readonly errorMessage = signal<string[]>([]);

  readonly dokumentTypen = input.required<DokumentTyp[]>();
  private readonly fileInput = viewChild.required<ElementRef>('fileInput');
  private readonly focusAfterUpload = viewChild.required<ElementRef>('focusAfterUpload');
  private selectedUpload: DokumentTyp|null = null;

  private readonly existingUploads = computed(() => {
    const existing = new Set(this.selectedDokumente().map( it => it.typ.id ));
    return this.dokumentTypen().filter( it => existing.has(it.id) );
  });

  private readonly remainingUploads = computed(() => {
    let existingTypen: number[] = this.selectedDokumente().map( it => it.typ.id );
    let remainingUploads: Map<DokumentTyp, number> = new Map<DokumentTyp, number>();
    this.dokumentTypen()?.forEach( typ => {
      let existing = existingTypen.filter( it => it === typ.id ).length;
      let remaining = typ.maxUploadCount - existing;
      if (remaining > 0) {
        remainingUploads.set(typ, remaining);
      }
    });
    return remainingUploads;
  });

  protected readonly availableUploads = computed(() => Array.from(this.remainingUploads().keys()));
  protected readonly showAlertImmunologisch = computed(() => this.availableUploads().some( it => it.id === DokumentTypId.LABORANALYSEN_AUTOVAKZINE ) );
  protected readonly showAlertLieferEngpass = computed(() => this.availableUploads().some( it => it.id === DokumentTypId.BESTAETIGUNG_LIEFERENGPASS ));
  protected readonly iconAlertImmunologisch = computed<string>(() => this.existingUploads().some( it => it.id === DokumentTypId.LABORANALYSEN_AUTOVAKZINE ) ? '' : 'icon icon--before icon--exclam red' );
  protected readonly iconAlertLieferEngpass = computed<string>(() => this.existingUploads().some( it => it.id === DokumentTypId.BESTAETIGUNG_LIEFERENGPASS ) ? '' : 'icon icon--before icon--exclam red' );

  readonly dokumenteChange = output<Dokument[]>();

  @Input()
  set dokumente(selectedDokumente: Dokument[]) {
    this.selectedDokumente.set(selectedDokumente);
  }

  selectDokument(typ: DokumentTyp): void {
    this.selectedUpload = typ;
    this.focusAfterUpload().nativeElement.focus();
    const remainingUploads = this.remainingUploads().get(typ) ?? 0;
    this.fileInput().nativeElement.multiple = remainingUploads > 1 ? 'multiple' : null;
    this.fileInput().nativeElement.click();
  }

  uploadDokument(event: Event): void {
    if (!this.selectedUpload) {
      throw new Error("No DokumentTyp selected");
    }
    const input = event.target as HTMLInputElement;
    const files: FileList = input.files ?? new FileList();
    const remainingUploads = this.remainingUploads().get(this.selectedUpload) ?? 0;
    if (files.length > remainingUploads) {
      this.errorMessage.set([
        this.translateService.instant('dokument.maxCount', {
          max   : this.remainingUploads().get(this.selectedUpload),
          count : files.length
        })
      ]);
    } else if (files.length) {
      this.errorMessage.set([]);
      for (let file of files) {
        this.backendService.uploadDokument(this.selectedUpload, file)
          .subscribe({
            next: dokument => {
              this.selectedDokumente.update( it => [...it, dokument] );
              this.dokumenteChange.emit(this.selectedDokumente());
            },
            error: () => this.errorMessage.update(it => [
              ...it,
              this.translateService.instant('dokument.failedUpload', {
                name: file.name
              })
            ])
          });
      }
      this.fileInput().nativeElement.value = null;
    }
  }

  downloadDokument(dokument: Dokument): void {
    this.backendService.downloadDokument(dokument)
      .subscribe({
        next: response => {
          downloadFile(response);
        },
        error: console.error
      });
  }

  deleteDokument(dokument: Dokument): void {
    Swal.fire({
      text: this.translateService.instant('dokument.delete.message', {name: dokument.name}),
      icon: 'warning',
      buttonsStyling: true,
      showCancelButton: true,
      confirmButtonText: this.translateService.instant('dokument.delete.yes'),
      cancelButtonText: this.translateService.instant('global.cancel')
    }).then((result) => {
      if (result.value) {
        this.backendService.deleteDokument(dokument)
          .subscribe( () => this.removeDeletedDokument(dokument));
      }
    });
  }

  private removeDeletedDokument(dokument: Dokument): void {
    this.selectedDokumente.update( list => list.filter( it => it.id !== dokument.id ) );
    this.dokumenteChange.emit(this.selectedDokumente());
  }
}
